import styled from 'styled-components';
import { isEmptyString } from 'utils/stringHelpers';
import { BasicSelect, BaseOption, SelectWrapper } from 'ui/selects';

import { Trigger } from '../conditionValue';
import { getHandlebarComparatorsFromFieldName } from '../conditionValue/utils';
import { useFields } from '../hooks/useFields';
import { ComparatorType } from '../../../domain';
import { comparatorToSymbol, comparatorToLabelTranslationKey } from '../../../constants';
import { useConditionComparatorController, useConditionFieldNameWatch } from '../../../hooks/form';
import { useTranslation } from '../../../hooks/useTranslation';
import { useFieldsContext } from '../../../contexts';

const StyledBasicSelect = styled(BasicSelect)`
  ${({ theme }) => theme.styles.body.body2};
  width: fit-content;
  ${SelectWrapper} {
    border: none;
    background: none;
    :hover {
      border-color: none;
    }
  }
`;

// add field type
const useBaseOptions = (comparators: ComparatorType[]): BaseOption<ComparatorType>[] => {
  const { t } = useTranslation();
  return comparators.map((comparator) => {
    const Suffix = comparatorToSymbol[comparator];
    const labelTranslationKey = comparatorToLabelTranslationKey[comparator];
    const label = t(labelTranslationKey);
    return {
      id: `${comparator}: ${label}`,
      label,
      value: comparator,
      Suffix,
    };
  });
};

export const DATA_TEST = {
  select: 'comparator-select',
};

export const ComparatorSelect = () => {
  const fieldName = useConditionFieldNameWatch();
  const {
    field: { onChange, value: comparator },
  } = useConditionComparatorController();
  const { isLoading } = useFieldsContext();
  const fields = useFields();
  const comparators = getHandlebarComparatorsFromFieldName(fieldName, fields);
  const options = useBaseOptions(comparators);
  const setComparator = (value: ComparatorType) => onChange({ target: { value } });

  if (isEmptyString(fieldName)) return null;

  return (
    <StyledBasicSelect
      options={options}
      Trigger={Trigger}
      data-test={DATA_TEST.select}
      defaultValues={[comparator]}
      isLoading={isLoading}
      suffixUsed
      onSelect={([value]) => {
        setComparator(value as ComparatorType);
      }}
    />
  );
};
